import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';
import TaskActivityLog from './TaskActivityLog';
import TaskComments from './TaskComments';
import type { TaskFull } from '@/redux/internal-services/workspaceApiExtensions';
import {
  useUpdateTaskStatusMutation,
  useUpdateTaskMutation,
  useFollowTaskMutation,
  useUnfollowTaskMutation,
  useClaimTaskMutation,
  useGetTaskFollowersQuery,
  useAssignTaskMutation,
  useReassignTaskMutation,
  useCreateReminderMutation,
  useGetRemindersQuery,
  useSnoozeReminderMutation,
  useCancelReminderMutation,
} from '@/redux/internal-services/workspaceApiExtensions';
import { useListTenantUsersQuery, useGetTenantUserByIdQuery, useListAssignableRolesQuery } from '@/redux/internal-services/onboardingApi';
import { useAppSelector } from '@/hooks';
import { PRIORITY_CFG, PriorityIcon } from '@/components/Tasks/TaskCard';

export type TaskDetailTab = 'details' | 'comments' | 'activity';
interface Props {
  task: TaskFull;
  /** When provided, the tab is controlled from outside (e.g. by SAM). */
  activeTab?: TaskDetailTab;
  onTabChange?: (tab: TaskDetailTab) => void;
  /** Called when the user clicks the "Linked to: Engagement" row to navigate to it. */
  onOpenEngagement?: (engagementId: string) => void;
  /** Hides the hero card (title/status/priority) — use when the card header already shows that info. */
  compact?: boolean;
}

const STATUS_CFG = {
  open:      { bg: 'bg-accent/10',  text: 'text-accent-text',  label: 'Open' },
  completed: { bg: 'bg-success/10', text: 'text-success-text', label: 'Done' },
  cancelled: { bg: 'bg-ui-hover', text: 'text-ui-text2',  label: 'Cancelled' },
} as const;

const TYPE_LABEL: Record<string, string> = {
  general: 'General', follow_up: 'Follow up', approval: 'Approval', review: 'Review',
};

const STATUS_OPTIONS = ['open', 'completed', 'cancelled'] as const;
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'] as const;
const TYPE_OPTIONS = Object.keys(TYPE_LABEL);
const editInputCls = fieldClass({ size: 'xs' });

// ── Detail row helper ────────────────────────────────────────────────────────

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-ui-border last:border-b-0">
    <div className="w-8 h-8 rounded-lg bg-ui-subtle border border-ui-border flex items-center justify-center flex-shrink-0 text-ui-text3">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-micro font-semibold text-ui-text3 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-ui-text">{children}</div>
    </div>
  </div>
);

// ── Main panel ───────────────────────────────────────────────────────────────

const TaskDetailPanel: React.FC<Props> = ({ task, activeTab, onTabChange, onOpenEngagement, compact }) => {
  const { t } = useTranslation();
  const userId = useAppSelector((s) => s.sessionState.userId);
  const userRoleIds = useAppSelector((s) => s.sessionState.roles);
  const { data: followers = [] } = useGetTaskFollowersQuery(task.task_id);
  // Assignee and follower are separate roles; legacy tasks may still list the assignee as a follower.
  const watcherCount = followers.filter(id => id !== task.assignee_user_id).length;
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateTaskStatusMutation();
  const [updateTask,   { isLoading: saving }]         = useUpdateTaskMutation();
  const [followTask]   = useFollowTaskMutation();
  const [unfollowTask] = useUnfollowTaskMutation();
  const [claimTask]    = useClaimTaskMutation();
  const [internalTab, setInternalTab] = useState<TaskDetailTab>('details');
  const tab = activeTab ?? internalTab;
  const setTab = (t: TaskDetailTab) => { setInternalTab(t); onTabChange?.(t); };

  // ── Edit state ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);

  const parseDate = (iso?: string | null) => {
    if (!iso) return { date: '', time: '' };
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString('en-CA'),
      time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
    };
  };

  const initEdit = () => {
    const { date, time } = parseDate(task.due_date);
    setEditTitle(task.title);
    setEditPriority(task.priority);
    setEditType(task.task_type);
    setEditStatus(task.status);
    setEditDate(date);
    setEditTime(time);
    setEditTags(task.tags ?? []);
    setEditing(true);
  };
  // Retained for the upcoming edit trigger; referenced here to keep it until wired into the UI.
  void initEdit;

  const [editTitle,    setEditTitle]    = useState(task.title);
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editType,     setEditType]     = useState(task.task_type);
  const [editStatus,   setEditStatus]   = useState(task.status);
  const [editDate,     setEditDate]     = useState('');
  const [editTime,     setEditTime]     = useState('');
  const [editTags,     setEditTags]     = useState<string[]>(task.tags ?? []);
  const [tagInput,     setTagInput]     = useState('');

  const addTag = () => {
    const v = tagInput.trim();
    if (v && !editTags.includes(v)) { setEditTags([...editTags, v]); setTagInput(''); }
  };

  const handleSave = async () => {
    if (!editTitle.trim()) return;
    const due_date = editDate ? (editTime ? `${editDate}T${editTime}` : editDate) : null;
    await updateTask({ id: task.task_id, body: { title: editTitle.trim(), priority: editPriority, task_type: editType, due_date, tags: editTags } });
    if (editStatus !== task.status) await updateStatus({ id: task.task_id, status: editStatus });
    setEditing(false);
  };

  // ── View helpers ────────────────────────────────────────────────────────────
  const isFollowing = followers.includes(userId || '');
  // Assignee is a separate role from follower (TSK-FR-04-012) — hide Follow toggle for them.
  // Owner/creator sees the toggle when the task is assigned to someone else.
  const isAssignee  = !!userId && task.assignee_user_id === userId;
  // Only a member of the queue's role may claim it (the API 403s anyone else).
  const canClaim    = !!task.queue_role_id && !task.assignee_user_id && userRoleIds.includes(task.queue_role_id);
  const canComplete = !['completed', 'cancelled'].includes(task.status) && !task.permission_denied;

  const pc  = PRIORITY_CFG[task.priority as keyof typeof PRIORITY_CFG] ?? PRIORITY_CFG.normal;
  const sc  = STATUS_CFG[task.status   as keyof typeof STATUS_CFG]     ?? { bg: 'bg-ui-subtle', text: 'text-ui-text2', label: task.status };
  const due = task.due_date ? new Date(task.due_date) : null;
  const now = new Date();
  const isOverdue = due && due < now && !['completed', 'cancelled'].includes(task.status);

  const editPc = PRIORITY_CFG[editPriority as keyof typeof PRIORITY_CFG] ?? PRIORITY_CFG.normal;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4 max-w-2xl mx-auto'}>

      {/* ── Hero card — hidden in compact mode (card header already shows title/status/priority) ── */}
      {!compact && (
      <div className="card-workspace overflow-hidden">
        <div className={`h-1 w-full ${editing ? editPc.bar : pc.bar}`} />

        <div className="p-4 space-y-3">
          {/* Title row */}
          <div className="flex items-start gap-3">
            {editing
              ? <input autoFocus value={editTitle} onChange={e => setEditTitle(e.target.value)}
                  className="flex-1 text-base font-bold bg-transparent border-b-2 border-accent/40 text-ui-text outline-none pb-0.5 leading-snug" />
              : <h1 className="flex-1 text-base font-bold text-ui-text leading-snug">{task.title}</h1>
            }
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Status — read pill or editable pills */}
              {editing ? (
                <div className="flex gap-1">
                  {STATUS_OPTIONS.map(s => {
                    const cfg = STATUS_CFG[s as keyof typeof STATUS_CFG] ?? { bg: 'bg-ui-hover', text: 'text-ui-text2', label: s };
                    return (
                      <button key={s} type="button" onClick={() => setEditStatus(s)}
                        className={`text-micro font-bold px-2.5 py-1 rounded-full uppercase tracking-wide border-2 transition-all ${
                          editStatus === s
                            ? `${cfg.bg} ${cfg.text} border-current`
                            : 'bg-transparent text-ui-text3 border-transparent hover:border-ui-border'
                        }`}>
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <span className={`text-micro font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${sc.bg} ${sc.text}`}>
                  {sc.label}
                </span>
              )}
            </div>
          </div>

          {/* Priority + type — editable selects or read chips */}
          {editing ? (
            <div className="grid grid-cols-2 gap-2">
              <select value={editPriority} onChange={e => setEditPriority(e.target.value)} className={editInputCls}>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} priority</option>)}
              </select>
              <select value={editType} onChange={e => setEditType(e.target.value)} className={editInputCls}>
                {TYPE_OPTIONS.map(tp => <option key={tp} value={tp}>{TYPE_LABEL[tp] ?? tp}</option>)}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`flex items-center gap-1.5 text-xs font-semibold ${pc.text}`}>
                <PriorityIcon level={task.priority} size={12} />
                {pc.label} priority
              </span>
              <span className="w-px h-3 bg-ui-hover" />
              <span className="text-xs text-ui-text2 capitalize font-medium">
                {TYPE_LABEL[task.task_type] ?? task.task_type.replace('_', ' ')}
              </span>
              {due && (
                <>
                  <span className="w-px h-3 bg-ui-hover" />
                  <span className={`flex items-center gap-1 text-xs font-medium ${isOverdue ? 'text-danger-text' : 'text-ui-text3'}`}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {isOverdue ? 'Overdue · ' : ''}
                    {due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    {' · '}
                    {due.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Due date + time — editable or action buttons */}
          {editing ? (
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className={editInputCls} />
              <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className={editInputCls} />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {canComplete && (
                <button onClick={() => updateStatus({ id: task.task_id, status: 'completed' })} disabled={updatingStatus}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-success/10 border border-success/30 text-success-text text-xs font-semibold hover:bg-success/15 transition-colors disabled:opacity-50">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  {t('tasks.complete', 'Mark done')}
                </button>
              )}
              {canClaim && (
                <button onClick={() => claimTask(task.task_id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-accent-soft/30 border border-accent/20 text-accent-text text-xs font-semibold hover:bg-accent-soft/50 transition-colors">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  {t('tasks.claim', 'Claim')}
                </button>
              )}
              {!isAssignee && <button onClick={() => isFollowing ? unfollowTask(task.task_id) : followTask(task.task_id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                  isFollowing ? 'bg-accent/5 border-accent/20 text-accent-text' : 'bg-ui-subtle border-ui-border text-ui-text2 hover:border-accent/20 hover:text-accent-text'
                }`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill={isFollowing ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {isFollowing ? t('tasks.unfollow', 'Following') : t('tasks.follow', 'Follow')}
              </button>}
              {canComplete && (
                <button onClick={() => updateStatus({ id: task.task_id, status: 'cancelled' })} disabled={updatingStatus}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-ui-subtle border border-ui-border text-ui-text3 text-xs font-medium hover:text-danger-text hover:border-danger/30 transition-colors ml-auto disabled:opacity-50">
                  {t('tasks.cancel', 'Cancel task')}
                </button>
              )}
            </div>
          )}

          {/* Tags edit */}
          {editing && (
            <div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {editTags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-soft/40 border border-accent-soft text-xs font-medium text-accent-text">
                    {tag}
                    <button type="button" onClick={() => setEditTags(editTags.filter(t2 => t2 !== tag))} className="text-accent-text/60 hover:text-accent-text leading-none ml-0.5">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Add tag…" className={editInputCls} />
                <button type="button" onClick={addTag}
                  className="px-3 py-2 rounded-lg bg-accent/10 text-accent-text text-xs font-semibold hover:bg-accent/20 transition-colors flex-shrink-0">Add</button>
              </div>
            </div>
          )}

          {/* Save / Cancel */}
          {editing && (
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setEditing(false)}
                className="flex-1 py-2 rounded-xl border border-ui-border text-sm font-semibold text-ui-text2 hover:bg-ui-subtle transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving || !editTitle.trim()}
                className="flex-1 py-2 rounded-xl btn-sheen text-on-primary text-sm font-semibold disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Compact action strip (shown instead of hero card) ── */}
      {compact && (
        <div className="flex flex-wrap gap-2 pt-1">
          {canComplete && (
            <button onClick={() => updateStatus({ id: task.task_id, status: 'completed' })} disabled={updatingStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-success/10 border border-success/30 text-success-text text-xs font-semibold hover:bg-success/15 transition-colors disabled:opacity-50">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {t('tasks.complete', 'Mark done')}
            </button>
          )}
          {!isAssignee && <button onClick={() => isFollowing ? unfollowTask(task.task_id) : followTask(task.task_id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors ${
              isFollowing ? 'bg-accent/5 border-accent/20 text-accent-text' : 'bg-ui-subtle border-ui-border text-ui-text2 hover:border-accent/20 hover:text-accent-text'
            }`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill={isFollowing ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            {isFollowing ? t('tasks.unfollow', 'Unfollow') : t('tasks.follow', 'Follow')}
          </button>}
          {canComplete && (
            <button onClick={() => updateStatus({ id: task.task_id, status: 'cancelled' })} disabled={updatingStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-ui-subtle border border-ui-border text-ui-text3 text-xs font-medium hover:text-danger-text hover:border-danger/30 transition-colors ml-auto disabled:opacity-50">
              {t('tasks.cancel', 'Cancel task')}
            </button>
          )}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-0 rounded-xl border border-ui-border bg-ui-subtle p-1">
        {(['details', 'comments', 'activity'] as const).map((t2) => (
          <button
            key={t2}
            onClick={() => setTab(t2)}
            className={`flex-1 py-2 text-xs font-semibold capitalize rounded-lg transition-colors ${
              tab === t2
                ? 'bg-ui-card text-accent-text shadow-sm border border-ui-border'
                : 'text-ui-text3 hover:text-ui-text'
            }`}
          >
            {t2}
          </button>
        ))}
      </div>

      {/* ── Details tab ── */}
      {tab === 'details' && (
        <div className="space-y-4">
          {/* Info card */}
          <div className="card-workspace px-4 py-1">

            {due && (
              <DetailRow
                label="Due date"
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
              >
                <span className={isOverdue ? 'text-danger-text font-medium' : ''}>
                  {due.toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric' })}
                  {' at '}
                  {due.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  {isOverdue && ' · overdue'}
                </span>
              </DetailRow>
            )}

            {task.context && (() => {
              const isEngagement = task.context.context_type === 'engagement';
              const canNavigate = isEngagement && !!onOpenEngagement;
              const label = (
                <>
                  <span className="capitalize">{task.context.context_type}</span>
                  {task.context.display_label && ` · ${task.context.display_label}`}
                </>
              );
              return (
                <DetailRow
                  label="Linked to"
                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>}
                >
                  {canNavigate ? (
                    <button
                      onClick={() => onOpenEngagement!(task.context!.entity_id)}
                      className="text-left text-accent-text hover:underline transition-opacity hover:opacity-80"
                    >
                      {label}
                    </button>
                  ) : (
                    <span>{label}</span>
                  )}
                </DetailRow>
              );
            })()}

            <DetailRow
              label="Followers"
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
            >
              {watcherCount} {watcherCount === 1 ? 'follower' : 'followers'}
            </DetailRow>

            {(() => {
              const visibleTags = task.tags.filter(t => t !== 'workflow');
              return visibleTags.length > 0 ? (
                <DetailRow
                  label="Tags"
                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>}
                >
                  <div className="flex flex-wrap gap-1">
                    {visibleTags.map((tag) => (
                      <span key={tag} className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent-text">
                        {tag}
                      </span>
                    ))}
                  </div>
                </DetailRow>
              ) : null;
            })()}

            <div className="py-2.5 flex flex-col gap-0.5">
              <p className="text-xs text-ui-text3">Created {new Date(task.created_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · Updated {new Date(task.updated_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Assignment */}
          <AssignmentSection task={task} />

          {/* Reminders */}
          <ReminderSection task={task} />
        </div>
      )}

      {/* ── Comments tab ── */}
      {tab === 'comments' && (
        <div className="card-workspace p-4">
          <TaskComments taskId={task.task_id} />
        </div>
      )}

      {/* ── Activity tab ── */}
      {tab === 'activity' && (
        <div className="card-workspace p-4">
          <TaskActivityLog taskId={task.task_id} />
        </div>
      )}
    </div>
  );
};

// ── Assignment section ───────────────────────────────────────────────────────

const AssignmentSection: React.FC<{ task: TaskFull }> = ({ task }) => {
  const { t } = useTranslation();
  const [assignTask] = useAssignTaskMutation();
  const [reassignTask] = useReassignTaskMutation();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState<'user' | 'role'>('user');
  const [search, setSearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');

  // Fetch assignee details to show name instead of UUID — query by exact user_id, not text search.
  const { data: assigneeUser } = useGetTenantUserByIdQuery(
    task.assignee_user_id ?? '',
    { skip: !task.assignee_user_id },
  );
  const assigneeName = assigneeUser?.name || assigneeUser?.email || 'Unknown user';
  const assigneeInitials = (assigneeUser?.name || assigneeUser?.email || '')
    .split(' ').filter(Boolean).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    || '?';

  const { data: usersData } = useListTenantUsersQuery(
    showPicker ? { search: search || undefined, page_size: 10 } : undefined,
    { skip: !showPicker },
  );

  // Resolve the queue's role name — the list is small and already tag-cached across the app,
  // so a lookup-by-id endpoint isn't worth adding just for this display.
  const isAssigned = !!task.assignee_user_id;
  const { data: assignableRolesRes } = useListAssignableRolesQuery(undefined, {
    skip: !showPicker && (isAssigned || !task.queue_role_id),
  });
  const queueRoleName = assignableRolesRes?.data?.find((r) => r.id === task.queue_role_id)?.name;
  const mutation = isAssigned || task.queue_role_id ? reassignTask : assignTask;

  const handleSelectUser = async (selectedUserId: string) => {
    await mutation({ id: task.task_id, assignee_user_id: selectedUserId });
    setShowPicker(false);
    setSearch('');
  };

  const handleSelectRole = async (selectedRoleId: string) => {
    await mutation({ id: task.task_id, queue_role_id: selectedRoleId });
    setShowPicker(false);
    setRoleSearch('');
  };

  return (
    <div className="card-workspace p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-ui-text3 uppercase tracking-wider">Assignee</p>
        {!task.assign_permission_denied && (
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="text-xs font-semibold text-accent-text hover:underline"
          >
            {isAssigned ? t('tasks.reassign', 'Reassign') : t('tasks.assign', 'Assign')}
          </button>
        )}
      </div>

      {/* Current assignee display */}
      <div className="flex items-center gap-2.5">
        {isAssigned ? (
          <>
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">{assigneeInitials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm text-ui-text font-medium truncate">{assigneeName}</p>
              {assigneeUser?.email && assigneeUser?.name && (
                <p className="text-xs text-ui-text3 truncate">{assigneeUser.email}</p>
              )}
            </div>
          </>
        ) : task.queue_role_id ? (
          <>
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm text-ui-text font-medium truncate">{queueRoleName || 'Role queue'}</p>
              <p className="text-xs text-ui-text3 truncate">Role queue</p>
            </div>
          </>
        ) : (
          <p className="text-sm text-ui-text3 italic">Unassigned</p>
        )}
      </div>

      {/* Search picker */}
      {showPicker && (
        <div className="space-y-2 border-t border-ui-border pt-3">
          <div className="flex border border-ui-border rounded-xl overflow-hidden">
            {(['user', 'role'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setPickerTab(tab)}
                className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                  pickerTab === tab ? 'text-accent-text bg-accent-soft/10' : 'text-ui-text3 hover:text-ui-text'
                }`}
              >
                {tab === 'user' ? 'Assign to user' : 'Role queue'}
              </button>
            ))}
          </div>

          {pickerTab === 'user' ? (
            <>
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search team members…"
                className="w-full rounded-xl border border-ui-border bg-ui-input px-3 py-2.5 text-sm text-ui-text font-poppins focus:outline-none focus:ring-2 focus:ring-ui-ring/30"
              />
              <div className="max-h-40 overflow-y-auto rounded-xl border border-ui-border bg-ui-card">
                {(usersData?.data ?? []).map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => handleSelectUser(u.user_id)}
                    className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 min-h-11 hover:bg-accent-soft/30 transition-colors border-b border-ui-border last:border-b-0"
                  >
                    <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-accent-text">
                        {(u.name || u.email || '?').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm text-ui-text">{u.name || u.email}</span>
                  </button>
                ))}
                {usersData && usersData.data.length === 0 && (
                  <p className="text-xs text-ui-text3 px-3 py-3">{t('common.noResults', 'No results')}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <input
                autoFocus
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                placeholder="Search roles…"
                className="w-full rounded-xl border border-ui-border bg-ui-input px-3 py-2.5 text-sm text-ui-text font-poppins focus:outline-none focus:ring-2 focus:ring-ui-ring/30"
              />
              <div className="max-h-40 overflow-y-auto rounded-xl border border-ui-border bg-ui-card">
                {(assignableRolesRes?.data ?? [])
                  .filter((r) => r.name.toLowerCase().includes(roleSearch.toLowerCase()))
                  .map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleSelectRole(r.id)}
                      className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 min-h-11 hover:bg-accent-soft/30 transition-colors border-b border-ui-border last:border-b-0"
                    >
                      <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-text"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-ui-text truncate">{r.name}</p>
                        {r.description && <p className="text-xs text-ui-text3 truncate">{r.description}</p>}
                      </div>
                    </button>
                  ))}
                {assignableRolesRes && assignableRolesRes.data.length === 0 && (
                  <p className="text-xs text-ui-text3 px-3 py-3">{t('common.noResults', 'No results')}</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Reminder section ─────────────────────────────────────────────────────────

const ReminderSection: React.FC<{ task: TaskFull }> = ({ task }) => {
  const { t } = useTranslation();
  const { data: remindersData } = useGetRemindersQuery({
    source_module: 'task',
    source_entity_id: task.task_id,
  });
  const [createReminder, { isLoading: creating }] = useCreateReminderMutation();
  const [snoozeReminder] = useSnoozeReminderMutation();
  const [cancelReminder] = useCancelReminderMutation();
  const [showCreate, setShowCreate] = useState(false);
  const [reminderDate, setReminderDate] = useState('');

  const reminders = remindersData?.data ?? [];
  const activeReminders = reminders.filter((r) => ['active', 'scheduled', 'snoozed'].includes(r.status));

  const handleCreate = async () => {
    if (!reminderDate) return;
    await createReminder({
      title: task.title, // REMINDER_DUE templates already prefix "Reminder: "
      dtstart: new Date(reminderDate).toISOString(),
      source_module: 'task',
      source_entity_type: 'task',
      source_entity_id: task.task_id,
      notification_code: 'REMINDER_DUE',
      action: { action_type: 'navigate', action_route: `/tasks/${task.task_id}` },
    });
    setReminderDate('');
    setShowCreate(false);
  };

  return (
    <div className="card-workspace p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-ui-text3 uppercase tracking-wider">
            {t('tasks.reminders', 'Reminders')}
          </p>
          {activeReminders.length > 0 && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-warning/10 text-warning-text">
              {activeReminders.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-xs font-semibold text-accent-text hover:underline"
        >
          + Add reminder
        </button>
      </div>

      {showCreate && (
        <div className="flex flex-col sm:flex-row gap-2 border-t border-ui-border pt-3">
          <input
            type="datetime-local"
            value={reminderDate}
            onChange={(e) => setReminderDate(e.target.value)}
            className="flex-1 rounded-xl border border-ui-border bg-ui-input px-3 py-2.5 text-sm text-ui-text font-poppins focus:outline-none focus:ring-2 focus:ring-ui-ring/30"
          />
          <Button size="sm" onClick={handleCreate} disabled={!reminderDate || creating}>
            {creating ? 'Setting…' : t('common.save', 'Save')}
          </Button>
        </div>
      )}

      {activeReminders.length === 0 && !showCreate && (
        <p className="text-xs text-ui-text3 italic">No active reminders</p>
      )}

      {activeReminders.map((r) => (
        <div key={r.id} className="rounded-xl border border-warning/30 bg-warning/10 px-3 py-2.5 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-warning-text flex-shrink-0">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-warning-text">
                  {r.next_fire_on
                    ? new Date(r.next_fire_on).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : 'Pending'}
                </p>
                {r.snooze_count > 0 && <p className="text-xs text-warning-text">Snoozed {r.snooze_count}×</p>}
              </div>
            </div>
            <button
              onClick={() => cancelReminder(r.id)}
              className="text-xs text-warning-text hover:text-danger-text transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-warning-text mr-1">Snooze:</span>
            {(['15m', '1h', '1d'] as const).map((d) => (
              <button
                key={d}
                onClick={() => snoozeReminder({ id: r.id, duration: d })}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-ui-card border border-warning/30 text-warning-text hover:bg-warning/15 transition-colors"
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TaskDetailPanel;
